import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import api from '../../services/api';
import { grey, primary, success, radius, shadows, fontWeight, fontSize } from '../../styles/tokens';

const SubscriptionDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCheckout = async () => {
    const priceId = process.env.REACT_APP_STRIPE_PRICE_ID;

    if (!priceId) {
      setError('Prix non configure. Contactez le support.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.post('/coach/auth/checkout', { priceId });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const features = [
    'Clients illimites',
    'Recettes personnalisees',
    'Plans alimentaires',
    'Support prioritaire',
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: radius.xl,
          boxShadow: shadows.lg,
          overflow: 'hidden',
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        <Typography
          sx={{
            fontSize: fontSize.xl,
            fontWeight: fontWeight.semibold,
            color: grey[900],
          }}
        >
          S'abonner
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: grey[400],
            '&:hover': { color: grey[600], bgcolor: grey[100] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: radius.md }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: fontSize.base,
            color: grey[500],
            textAlign: 'center',
            mb: 3,
          }}
        >
          Debloquez toutes les fonctionnalites
        </Typography>

        {/* Price */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            component="span"
            sx={{
              fontSize: '3rem',
              fontWeight: fontWeight.bold,
              color: grey[900],
              lineHeight: 1,
            }}
          >
            19€
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: fontSize.md,
              color: grey[500],
              ml: 0.5,
            }}
          >
            /mois
          </Typography>
        </Box>

        {/* Features */}
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: radius.full,
                  bgcolor: success[50],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CheckIcon sx={{ fontSize: 14, color: success.main }} />
              </Box>
              <Typography
                sx={{
                  fontSize: fontSize.base,
                  color: grey[700],
                }}
              >
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* CTA Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleCheckout}
          disabled={loading}
          sx={{
            py: 1.5,
            borderRadius: radius.lg,
            bgcolor: primary[500],
            fontSize: fontSize.base,
            fontWeight: fontWeight.medium,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: primary[600],
              boxShadow: 'none',
            },
            '&:disabled': {
              bgcolor: grey[200],
              color: grey[500],
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : (
            "S'abonner maintenant"
          )}
        </Button>

        {/* Footer note */}
        <Typography
          sx={{
            fontSize: fontSize.sm,
            color: grey[400],
            textAlign: 'center',
            mt: 2,
          }}
        >
          Annulation possible a tout moment
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SubscriptionDialog;
